/**
 * The client half of a resumable upload: send a blob in chunks, resume after a drop.
 *
 * Shared by both apps rather than copied into each, because this is a *protocol*
 * implementation, not app code. The three numbers that matter — where a 308's `Range`
 * says the server is, that the range is inclusive so the next offset is its end plus
 * one, and that a chunk must start exactly where the server is — are the kind of detail
 * two copies get subtly different and only production notices.
 *
 * The server decides whether resumability is on offer: a reserve response carries an
 * `upload` block or it does not. When it does not, the caller PUTs the whole blob as
 * before. Nothing here decides policy.
 *
 * What this is NOT: continuous recording, `timeslice`, or IndexedDB persistence. Those
 * change how the recorder behaves and belong to the design workstream. This module
 * takes a finished blob and sends it well.
 */

/** The `upload` block a reserve response carries when a session is on offer. */
export interface ResumableUploadOffer {
    /**
     * `content-range` — sequential PUTs carrying `Content-Range` (GCS, and the local dev
     * route). `azure-blocks` — stage numbered blocks, then commit the list. These are
     * different client paths, not variations on one, which is why the server names it
     * rather than leaving the client to infer it from the URL.
     */
    protocol: string
    /** Where chunks go. Carries its own credential — never log it. */
    url: string
    /**
     * Size of every chunk but the last. Provider-constrained (GCS rejects a non-final
     * chunk that is not a multiple of 256 KiB), so it is served, not chosen here.
     */
    chunkBytes: number
    expiresAt?: string | null
}

export interface ResumableUploadOptions {
    /** 0–100, called after each chunk lands. Same contract as the single-shot path. */
    onProgress?: (progress: number) => void
    /** Lets a caller cancel; the loop checks between chunks. */
    signal?: AbortSignal
    /** Same-origin URLs need the auth cookie; a signed cloud URL must not get it. */
    withCredentials?: boolean
}

/** Only this protocol has a client implementation here. */
export const CONTENT_RANGE = 'content-range'

/**
 * True when this offer can actually be used by {@link uploadResumable}.
 *
 * `azure-blocks` is a genuinely different flow (stage blocks, then commit a list) and is
 * deliberately not implemented: no environment serves it today, and a half-written
 * version would be worse than falling back to a single PUT that definitely works.
 */
export function canResume(offer: ResumableUploadOffer | null | undefined): boolean {
    return !!offer && offer.protocol === CONTENT_RANGE && !!offer.url && offer.chunkBytes > 0
}

/** A resumable upload could not be completed; the caller may retry or fall back. */
export class ResumableUploadError extends Error {
    readonly status?: number

    constructor(message: string, status?: number) {
        super(message)
        this.name = 'ResumableUploadError'
        this.status = status
    }
}

/**
 * Ask the server how much it already holds.
 *
 * An empty PUT whose `Content-Range` is the pure status form (`bytes` followed by two
 * wildcards) is how the protocol asks "where am I". The answers are not
 * interchangeable:
 *
 * - **308** with a `Range` header — that many bytes are committed. The header is
 *   *inclusive* (`bytes=0-99` is 100 bytes), so the offset is its end **plus one**.
 *   Reading it as the offset directly re-sends one byte on every resume, which corrupts
 *   the object rather than failing.
 * - **308** with no `Range` — nothing committed yet. Offset 0.
 * - **200/201** — already complete.
 *
 * Always asked before sending, never assumed: a client that tracks its own progress is
 * wrong about any chunk the server received but did not acknowledge.
 */
export async function fetchCommittedOffset(
    offer: ResumableUploadOffer,
    options: ResumableUploadOptions = {}
): Promise<number | 'complete'> {
    const response = await fetch(offer.url, {
        method: 'PUT',
        headers: { 'Content-Range': 'bytes */*' },
        credentials: options.withCredentials ? 'include' : 'same-origin',
        signal: options.signal,
    })

    if (response.status === 200 || response.status === 201) return 'complete'
    if (response.status !== 308) {
        throw new ResumableUploadError(
            `Could not read upload offset (status ${response.status}); the session may have expired`,
            response.status
        )
    }

    const range = response.headers.get('Range')
    if (!range) return 0
    const end = Number(range.slice(range.lastIndexOf('-') + 1))
    if (!Number.isFinite(end)) {
        throw new ResumableUploadError(`Malformed Range header from server: ${range}`)
    }
    return end + 1
}

/**
 * Send `blob` to the session, resuming from whatever the server already holds.
 *
 * Safe to call again after a failure: it re-asks for the offset and continues, which is
 * the entire point — a dropped connection costs one chunk, not the whole file.
 */
export async function uploadResumable(
    offer: ResumableUploadOffer,
    blob: Blob,
    options: ResumableUploadOptions = {}
): Promise<void> {
    if (!canResume(offer)) {
        throw new ResumableUploadError(`Unsupported resumable protocol: ${offer?.protocol}`)
    }

    const total = blob.size
    if (total === 0) throw new ResumableUploadError('Refusing to upload an empty recording')

    const start = await fetchCommittedOffset(offer, options)
    if (start === 'complete') {
        options.onProgress?.(100)
        return
    }

    let offset = start
    // A server ahead of the blob we hold means the two disagree about what is being
    // uploaded. Continuing would append past the end or silently truncate.
    if (offset > total) {
        throw new ResumableUploadError(
            `Server holds ${offset} bytes but this recording is only ${total}`
        )
    }
    options.onProgress?.(Math.round((offset / total) * 100))

    while (offset < total) {
        if (options.signal?.aborted) throw new ResumableUploadError('Upload aborted')

        const end = Math.min(offset + offer.chunkBytes, total)
        const isFinal = end === total
        const response = await fetch(offer.url, {
            method: 'PUT',
            // The total is only declared on the final chunk. Until then it is `*`,
            // which is what lets a recording still being captured upload as it goes.
            headers: {
                'Content-Range': `bytes ${offset}-${end - 1}/${isFinal ? total : '*'}`,
            },
            body: blob.slice(offset, end),
            credentials: options.withCredentials ? 'include' : 'same-origin',
            signal: options.signal,
        })

        if (isFinal) {
            if (response.status !== 200 && response.status !== 201) {
                throw new ResumableUploadError(
                    `Final chunk rejected with status ${response.status}`,
                    response.status
                )
            }
            options.onProgress?.(100)
            return
        }

        if (response.status !== 308) {
            throw new ResumableUploadError(
                `Chunk at ${offset} rejected with status ${response.status}`,
                response.status
            )
        }

        // Trust the server's accounting over our own, every time. A 416 or a partial
        // accept moves the offset somewhere we did not predict, and re-deriving it here
        // is what keeps the object contiguous.
        const committed = await fetchCommittedOffset(offer, options)
        if (committed === 'complete') {
            options.onProgress?.(100)
            return
        }
        if (committed <= offset) {
            throw new ResumableUploadError(
                `Upload stalled: server still at ${committed} after sending through ${end - 1}`
            )
        }
        offset = committed
        options.onProgress?.(Math.round((offset / total) * 100))
    }
}
